import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash/fp';
import Modal from 'react-modal';
import classnames from 'classnames';

import styles from './SharedAdmin.module.scss';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { capitaliseStartLetter } from '../../../utils/regex-utils';
import { DraggableItem } from '../../shared/drag-n-drop/shared';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { IconButton } from '../../shared/button/IconButton';
import { CaretUp, Ellipsis, Pencil } from '../../shared/icons';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { DragDrop } from '../../shared/drag-n-drop/DragDrop';
import { Button } from '../../shared/button/Button';
import { UnsavedChangesModal } from '../../shared/modal/UnsavedChangesModal';
import { CalculatorPromptDB } from '../calculator/store';
import { CALCULATOR_FEW_SHOT_LIMIT } from './CalculatorPrompts';

const { white, primary } = styles;

const MODAL_HEADER = 56;
const PROMPT_HEIGHT = 92;
const BUTTONS = 48;

interface CalculatorPromptReorderModalProps {
    isOpen: boolean;
    closeModal: () => void;
    savedPromptOrder: CalculatorPromptDB[];
    currentPromptOrder: CalculatorPromptDB[];
    saveOrder: () => void;
    setOrder: (order: CalculatorPromptDB[]) => void;
}

export const CalculatorPromptReorderModal: React.FC<CalculatorPromptReorderModalProps> = ({ isOpen, closeModal, savedPromptOrder, currentPromptOrder, saveOrder, setOrder }) => {
    const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState<boolean>(false);
    const [overflowPrompts, setOverflowPrompts] = useState<number[]>([]);
    const [expandedPrompts, setExpandedPrompts] = useState<number[]>([]);
    const [screenWidth, screenHeight] = useWindowResize();

    const promptRefs = useRef(new Map<number, HTMLDivElement>());

    const setPromptRef = useCallback((id: number) => (prompt: HTMLDivElement | null) => prompt ? promptRefs.current.set(id, prompt) : promptRefs.current.delete(id), []);

    const draggablePromptWidth = useMemo(() => `${(screenWidth * 0.8) - 40}px`, [screenWidth]);
    const modalHeight = useMemo(() => screenHeight * 0.9, [screenHeight]);
    const maxPromptHeight = useMemo(() => modalHeight - (MODAL_HEADER + BUTTONS + (PROMPT_HEIGHT * 3)), [modalHeight]);
    const promptFieldType = useMemo(() => currentPromptOrder.length > 0 && capitaliseStartLetter(currentPromptOrder[0].calculatorFieldType) || '', [currentPromptOrder]);

    const orderUpdated = useMemo(() => !isEqual(savedPromptOrder, currentPromptOrder), [savedPromptOrder, currentPromptOrder]);
    const close = useCallback(() => {
        if (orderUpdated) {
            setUnsavedChangesModalOpen(true);
        } else {
            closeModal();
            setOverflowPrompts([]);
            setExpandedPrompts([]);
        }
    }, [orderUpdated, setUnsavedChangesModalOpen, closeModal]);

    const save = useCallback(() => {
        saveOrder();
        closeModal();
    }, [saveOrder, closeModal]);

    const confirmAndClose = useCallback(() => {
        setUnsavedChangesModalOpen(false);
        setOrder(savedPromptOrder);
        closeModal();
    }, [closeModal, setOrder, savedPromptOrder]);

    const listOptions = useMemo(() => currentPromptOrder.map(({ promptId, calculationText }) => ({ id: promptId?.toString() || '', label: calculationText })), [currentPromptOrder]);

    const updateList = (list: DraggableItem[]) => {
        const newOrder = list.map(({ id }) => currentPromptOrder.find(tile => tile.promptId!.toString() === id)!);
        setOrder(newOrder);
    };

    const expandPromptText = useCallback((promptId: number) => {
        const alreadyExpanded = expandedPrompts.includes(promptId);
        const updatedExpandedPrompts = alreadyExpanded ? expandedPrompts.filter(id => id !== promptId) : [...expandedPrompts, promptId];
        setExpandedPrompts(updatedExpandedPrompts);
    }, [expandedPrompts]);

    const getChildElement = useCallback((childId: string, index: number) => {
        const prompt = currentPromptOrder.find(({ promptId }) => promptId!.toString() === childId);
        if (prompt) {
            const priorityOrder = index + 1;
            const { promptId, calculationText } = prompt;
            const overflowed = overflowPrompts.includes(promptId);
            const isExpanded = expandedPrompts.includes(promptId);
            const isActivePrompt = priorityOrder <= CALCULATOR_FEW_SHOT_LIMIT;
            const showDivider = priorityOrder === CALCULATOR_FEW_SHOT_LIMIT && currentPromptOrder.length > CALCULATOR_FEW_SHOT_LIMIT;
            const iconFill = isActivePrompt ? white : primary;
            return (
                <>
                    <div className={classnames(styles.promptTile, { [styles.isActivePromptTile]: isActivePrompt })}>
                        <div className={classnames(styles.priorityOrder, { [styles.activePriority]: isActivePrompt })}>{priorityOrder}</div>
                        <div id={`prompt-${promptId}`} ref={setPromptRef(promptId)} className={classnames(styles.prompt, { [styles.overflowedPrompt]: overflowed, [styles.expandedPrompt]: isExpanded })} style={{ maxHeight: maxPromptHeight }}>
                            {isExpanded ? <Scrollable maxHeight={`${maxPromptHeight}px`}><>{calculationText}</></Scrollable> : calculationText}
                        </div>
                        {overflowed && !isExpanded && <div className={styles.ellipsisIconWrapper}><IconButton color={iconFill} fontSize={24} icon={Ellipsis} onClick={() => expandPromptText(promptId)} /></div>}
                        {overflowed && isExpanded && <div className={styles.caretIconWrapper}><IconButton color={iconFill} fontSize={24} icon={CaretUp} onClick={() => expandPromptText(promptId)} /></div>}
                    </div>
                    {showDivider &&
                        <div className={styles.divider}>
                            <div className={styles.dividerLine} />
                            <div className={styles.dividerLabel}>Not Included</div>
                        </div>
                    }
                </>
            );
        }
        return null;
    }, [currentPromptOrder, overflowPrompts, expandPromptText, expandedPrompts, maxPromptHeight, setPromptRef]);

    const checkForOverflowPrompts = useCallback(() => {
        setTimeout(() => {
            const ids = currentPromptOrder
                .filter(({ promptId }) => {
                    const el = promptRefs.current.get(promptId);
                    return !!el && el.scrollHeight > el.clientHeight;
                })
                .map(({ promptId }) => promptId);
            setOverflowPrompts(ids);
        }, 25);
    }, [currentPromptOrder]);

    useEffect(() => {
        if (isOpen) {
            checkForOverflowPrompts();
        }
    }, [checkForOverflowPrompts, isOpen, screenWidth]);

    useEffect(() => () => {
        setOverflowPrompts([]);
        setExpandedPrompts([]);
    }, []);

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                className={styles.reorderModal}
                ariaHideApp={false}
                style={{ overlay: { display: 'flex', zIndex: 9 } }}
                shouldCloseOnOverlayClick={false}
                shouldCloseOnEsc={false}
                onRequestClose={closeModal}
            >
                <div className={styles.reorderWrapper}>
                    <ModalHeader label={`Reorder ${promptFieldType} Prompts`} icon={Pencil} />
                    <div className={styles.scrollableWrapper}>
                        <DragDrop getChildElement={getChildElement} list={listOptions} listId='aiQueries' updateList={updateList} draggableItemHeight='70px' draggableItemWidth={draggablePromptWidth} />
                    </div>
                    <div className={styles.buttonsWrapper}>
                        <Button onClick={close} label='Close' />
                        <Button onClick={save} disabled={!orderUpdated} label='Save and Close' />
                    </div>
                </div>
            </Modal>
            <UnsavedChangesModal isOpen={unsavedChangesModalOpen} closeModal={() => setUnsavedChangesModalOpen(false)} confirm={confirmAndClose} />
        </>
    );
};
